/**
 * WebRekorderPage – Web-Rekorder (Welle 67, experimentell).
 *
 * "Browser im Browser": Ein Chromium läuft auf dem Server, sein Bild wird als
 * JPEG gepollt (kein WebSocket) und in ein <img> gezeichnet; Klicks/Tasten
 * gehen zurück an die Session. Man nimmt einen Ablauf auf, markiert die
 * Login-Stelle, speichert. Beim Abspielen pausiert der Lauf an der Login-
 * Stelle — man meldet sich selbst an und klickt "weiter".
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/config";

const API = `${API_BASE}/rekorder/api`;

const S = {
  page: { maxWidth: 1400, margin: "0 auto", padding: "24px 20px 60px", fontFamily: "inherit", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 20px", fontSize: "0.9rem" },
  card: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  cardTitle: { fontSize: "1.05rem", fontWeight: 600, margin: "0 0 12px" },
  row: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  input: { padding: "7px 10px", border: "1px solid var(--color-gray-300)", borderRadius: 6, fontSize: "0.9rem", background: "var(--bg-card)", color: "var(--color-gray-800)" },
  btn: { padding: "8px 15px", border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--color-gray-100)", color: "var(--color-gray-800)", cursor: "pointer", fontSize: "0.88rem", fontWeight: 600 },
  btnPrimary: { padding: "8px 15px", border: "none", borderRadius: 6, background: "var(--color-success)", color: "#fff", cursor: "pointer", fontSize: "0.88rem", fontWeight: 600 },
  btnRot: { padding: "8px 15px", border: "none", borderRadius: 6, background: "var(--color-error)", color: "#fff", cursor: "pointer", fontSize: "0.88rem", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "7px 10px", borderBottom: "2px solid var(--color-gray-300)", color: "var(--color-gray-700)" },
  td: { padding: "6px 10px", borderBottom: "1px solid var(--color-gray-200)" },
  err: { background: "var(--color-error-bg)", border: "1px solid var(--color-error)", borderRadius: 6, padding: "8px 12px", fontSize: "0.85rem", color: "var(--color-error)", marginBottom: 8 },
  info: { background: "var(--color-info-bg)", border: "1px solid var(--color-info)", borderRadius: 6, padding: "8px 12px", fontSize: "0.85rem", color: "var(--color-info)", marginBottom: 8 },
  browser: { border: "2px solid var(--color-gray-400)", borderRadius: 8, overflow: "hidden", background: "#000", display: "inline-block", maxWidth: "100%" },
  schrittZeile: { padding: "4px 8px", borderBottom: "1px solid var(--color-gray-200)", fontSize: "0.78rem", display: "flex", gap: 8 },
};

export default function WebRekorderPage() {
  const [aufnahmen, setAufnahmen] = useState([]);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [neuUrl, setNeuUrl] = useState("");
  const [neuName, setNeuName] = useState("");

  // Live-Session
  const [aktiv, setAktiv] = useState(null);   // {aufnahme, session_id, modus, lauf_id}
  const [zustand, setZustand] = useState(null);
  const [urlEingabe, setUrlEingabe] = useState("");
  const imgRef = useRef(null);
  const frameTimer = useRef(null);
  const zustandTimer = useRef(null);

  const [robotText, setRobotText] = useState(null);
  const [schrittEditor, setSchrittEditor] = useState(null); // {aufnahme, schritte:[]}

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`${API}/aufnahmen`);
      const d = await r.json();
      if (r.ok) setAufnahmen(d.aufnahmen || []);
    } catch (e) { setFehler(`Laden: ${e.message}`); }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // Frame-Polling, solange eine Session aktiv ist
  useEffect(() => {
    if (!aktiv?.session_id) return;
    const holeFrame = () => {
      if (imgRef.current) {
        imgRef.current.src = `${API}/session/${aktiv.session_id}/frame?t=${Date.now()}`;
      }
    };
    frameTimer.current = setInterval(holeFrame, 400);
    const holeZustand = async () => {
      try {
        const r = await fetch(`${API}/session/${aktiv.session_id}/zustand`);
        if (r.ok) setZustand(await r.json());
      } catch (_) {}
    };
    zustandTimer.current = setInterval(holeZustand, 1000);
    holeZustand();
    return () => { clearInterval(frameTimer.current); clearInterval(zustandTimer.current); };
  }, [aktiv]);

  const neuAnlegen = async () => {
    setFehler("");
    let url = neuUrl.trim();
    if (url && !url.startsWith("http")) url = "https://" + url;
    try {
      const r = await fetch(`${API}/aufnahmen`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: neuName.trim() || "Neue Aufnahme", start_url: url }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.status);
      setNeuUrl(""); setNeuName("");
      await laden();
    } catch (e) { setFehler(`Anlegen: ${e.message}`); }
  };

  const sessionStarten = async (a, modus) => {
    setFehler(""); setMeldung(""); setRobotText(null);
    try {
      const pfad = modus === "replay"
        ? `${API}/aufnahmen/${a.id}/abspielen`
        : `${API}/aufnahmen/${a.id}/session`;
      const r = await fetch(pfad, { method: "POST" });
      // Antwort erst als Text lesen: Kommt vom Server eine HTML-Fehlerseite (Proxy-Timeout,
      // Server-Browser nicht startbar), liefert r.json() sonst nur Safaris kryptisches
      // „The string did not match the expected pattern." statt der eigentlichen Ursache.
      const txt = await r.text();
      let d = null;
      try { d = JSON.parse(txt); } catch { /* keine JSON-Antwort */ }
      if (!d) {
        setFehler(`Der Server-Browser konnte nicht gestartet werden (Status ${r.status}, `
          + `keine JSON-Antwort). Den Grund nennt das Server-Protokoll — er steht dort als `
          + `letzter Fehler zum Aufruf /rekorder/api/aufnahmen/.../session.`);
        return;
      }
      if (!r.ok) throw new Error(d.detail || r.status);
      setAktiv({ aufnahme: a, session_id: d.session_id, modus, lauf_id: d.lauf_id });
      setUrlEingabe(a.start_url || "");
    } catch (e) { setFehler(`Session: ${e.message}`); }
  };

  const eingabe = async (body) => {
    if (!aktiv?.session_id) return;
    try {
      await fetch(`${API}/session/${aktiv.session_id}/eingabe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch (_) {}
  };

  // Darf ins Bild getippt/geklickt werden? Beim Aufnehmen immer; beim Abspielen
  // NUR während der Login-Pause (dann meldet sich der User selbst an).
  const darfEingeben = () => aktiv?.modus === "aufnahme" || !!zustand?.wartet_login;

  // Klick im Bild → Server-Viewport-Pixel (Bild wird evtl. skaliert dargestellt)
  const bildKlick = (ev) => {
    if (!darfEingeben()) return;
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round((ev.clientX - rect.left) * img.naturalWidth / rect.width);
    const y = Math.round((ev.clientY - rect.top) * img.naturalHeight / rect.height);
    eingabe({ typ: "klick", x, y });
  };

  const bildTaste = (ev) => {
    if (!darfEingeben()) return;
    if (ev.key.length === 1) { eingabe({ typ: "text", wert: ev.key }); ev.preventDefault(); }
    else if (["Enter", "Tab", "Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(ev.key)) {
      eingabe({ typ: "taste", wert: ev.key }); ev.preventDefault();
    }
  };

  const bildScroll = (ev) => {
    if (!darfEingeben()) return;
    eingabe({ typ: "scroll", dy: Math.round(ev.deltaY) });
  };

  const speichern = async () => {
    if (!aktiv) return;
    setFehler("");
    let r, txt;
    try {
      r = await fetch(`${API}/aufnahmen/${aktiv.aufnahme.id}/speichern`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: aktiv.session_id }),
      });
      txt = await r.text();
    } catch (e) {
      setFehler(`Speichern (Netzwerk): ${e.message}. Sitzung evtl. abgelaufen — bitte neu aufnehmen.`);
      return;
    }
    let d;
    try { d = JSON.parse(txt); }
    catch {
      // Kein JSON → klare Meldung statt kryptischem Safari-Parse-Fehler
      setFehler(`Speichern fehlgeschlagen (Status ${r.status}). Die Aufnahme-Sitzung ist wahrscheinlich abgelaufen — bitte den Ablauf neu aufnehmen.`);
      return;
    }
    if (!r.ok) { setFehler(`Speichern: ${d.detail || r.status}`); return; }
    setRobotText(d.robot);
    setMeldung(`Gespeichert: ${d.schritt_anzahl} Schritte, .robot erzeugt.`);
    setAktiv(null); setZustand(null);
    laden().catch(() => {});
  };

  const weiter = async () => {
    if (!aktiv?.session_id) return;
    await fetch(`${API}/session/${aktiv.session_id}/weiter`, { method: "POST" });
  };

  const beenden = () => { setAktiv(null); setZustand(null); };

  const loeschen = async (a) => {
    if (!window.confirm(`Aufnahme „${a.name}" löschen?`)) return;
    await fetch(`${API}/aufnahmen/${a.id}`, { method: "DELETE" });
    await laden();
  };

  const robotZeigen = async (a) => {
    const r = await fetch(`${API}/aufnahmen/${a.id}/robot`);
    const d = await r.json();
    setRobotText(d.robot);
  };

  // Schritt live (während der Aufnahme) entfernen
  const liveSchrittWeg = async (index) => {
    if (!aktiv?.session_id) return;
    await fetch(`${API}/session/${aktiv.session_id}/schritt-weg`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ index }),
    });
  };

  // Schritt-Editor für eine gespeicherte Aufnahme öffnen
  const schritteBearbeiten = async (a) => {
    setFehler(""); setRobotText(null);
    try {
      const r = await fetch(`${API}/aufnahmen/${a.id}/schritte`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.status);
      setSchrittEditor({ aufnahme: a, schritte: d.schritte || [] });
    } catch (e) { setFehler(`Schritte laden: ${e.message}`); }
  };

  // Einen Schritt aus dem Editor löschen und neue Liste speichern (.robot neu)
  const editorSchrittWeg = async (idx) => {
    const neu = schrittEditor.schritte.filter((_, i) => i !== idx);
    try {
      const r = await fetch(`${API}/aufnahmen/${schrittEditor.aufnahme.id}/schritte`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schritte: neu }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || r.status);
      setSchrittEditor((p) => ({ ...p, schritte: d.schritte || [] }));
      await laden();
    } catch (e) { setFehler(`Schritt entfernen: ${e.message}`); }
  };

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Web-Rekorder <span style={{ fontSize: "0.8rem", color: "var(--color-warning)" }}>experimentell</span></h1>
      <p style={S.sub}>
        Ein Browser läuft auf dem Server, du siehst ihn hier live und nimmst einen Ablauf auf
        (Domain öffnen, klicken, tippen, herunterladen). Die Anmeldung markierst du mit dem
        Login-Schalter — beim Abspielen pausiert der Ablauf dort, du meldest dich selbst an.
      </p>

      {fehler && <div style={S.err}>{fehler}</div>}
      {meldung && <div style={S.info}>{meldung}</div>}

      {/* Live-Browser */}
      {aktiv && (
        <div style={S.card}>
          <div style={{ ...S.row, justifyContent: "space-between", marginBottom: 10 }}>
            <div style={S.row}>
              <input style={{ ...S.input, width: 340 }} value={urlEingabe}
                     onChange={(e) => setUrlEingabe(e.target.value)}
                     placeholder="https://…"
                     onKeyDown={(e) => e.key === "Enter" && eingabe({ typ: "goto", url: urlEingabe })} />
              <button style={S.btn} onClick={() => eingabe({ typ: "goto", url: urlEingabe })}>Gehe zu</button>
              <span style={{ fontSize: "0.8rem", color: "var(--color-gray-600)" }}>
                {zustand?.url || ""}
              </span>
            </div>
            <div style={S.row}>
              {aktiv.modus === "aufnahme" && (
                <>
                  <button
                    style={zustand?.login_modus ? S.btnRot : S.btn}
                    onClick={() => eingabe({ typ: "login_modus", an: !zustand?.login_modus })}>
                    {zustand?.login_modus ? "🔑 Login-Modus AN" : "🔑 Anmeldung markieren"}
                  </button>
                  <button style={S.btnPrimary} onClick={speichern}>Beenden & speichern</button>
                </>
              )}
              {aktiv.modus === "replay" && zustand?.wartet_login && (
                <button style={S.btnPrimary} onClick={weiter}>Angemeldet → weiter</button>
              )}
              <button style={S.btn} onClick={beenden}>Schließen</button>
            </div>
          </div>

          {aktiv.modus === "replay" && (
            <div style={S.info}>
              Abspielen: Schritt {zustand?.replay_index || 0}/{zustand?.replay_gesamt || 0}
              {zustand?.wartet_login && " — bitte im Bild anmelden, dann „weiter“."}
              {zustand?.status === "ok" && " — fertig ✓"}
              {zustand?.status === "fehler" && ` — Fehler: ${zustand?.meldung}`}
            </div>
          )}

          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div
              style={{ ...S.browser, cursor: darfEingeben() ? "crosshair" : "default" }}
              tabIndex={0}
              onKeyDown={bildTaste}
              onWheel={bildScroll}>
              <img ref={imgRef} alt="Browser" onClick={bildKlick}
                   style={{ display: "block", width: 900, maxWidth: "100%", height: "auto" }} />
            </div>
            <div style={{ flex: 1, minWidth: 260, maxHeight: 560, overflowY: "auto" }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Schritte ({zustand?.schritt_anzahl || 0})
              </div>
              {zustand?.downloads?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-gray-600)", marginBottom: 3 }}>
                    Heruntergeladene Dateien (auf PC speichern):
                  </div>
                  {zustand.downloads.map((name, i) => (
                    <a key={i}
                       href={`${API}/session/${aktiv.session_id}/datei/${encodeURIComponent(name)}`}
                       download={name}
                       style={{ display: "inline-block", fontSize: "0.8rem",
                                color: "var(--color-success)", textDecoration: "none",
                                border: "1px solid var(--color-success)", borderRadius: 6,
                                padding: "2px 8px", marginRight: 6, marginBottom: 4 }}>
                      ⬇ {name}
                    </a>
                  ))}
                </div>
              )}
              {aktiv.modus === "aufnahme" && (zustand?.schritte || []).map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6,
                    padding: "4px 6px", borderBottom: "1px solid var(--color-gray-200)",
                    fontSize: "0.78rem" }}>
                  <span style={{ color: "var(--color-gray-500)", minWidth: 18 }}>{i + 1}</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={s.text}>{s.text}</span>
                  <button style={{ ...S.btn, padding: "1px 7px", color: "var(--color-error)" }}
                          title="Diesen Schritt entfernen"
                          onClick={() => liveSchrittWeg(i)}>×</button>
                </div>
              ))}
              <div style={{ fontSize: "0.75rem", color: "var(--color-gray-600)", marginTop: 8 }}>
                {aktiv.modus === "aufnahme"
                  ? "Ins Bild klicken/tippen wird aufgenommen. Einmal klicken, kurz warten bis sich das Bild bewegt, dann weiter. Für Tastatur zuerst ins Bild klicken. Falsche Schritte mit × entfernen."
                  : zustand?.wartet_login
                  ? "🔑 Jetzt anmelden: Zuerst ins Anmeldefeld im Bild klicken, dann Benutzername/Passwort tippen und einloggen. Wenn du eingeloggt bist, oben auf den grünen Knopf Angemeldet → weiter klicken."
                  : "Der Ablauf läuft automatisch."}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Neue Aufnahme */}
      {!aktiv && (
        <div style={S.card}>
          <div style={S.cardTitle}>Neue Aufnahme</div>
          <div style={S.row}>
            <input style={{ ...S.input, width: 220 }} placeholder="Name (z. B. Signal Iduna Postfach)"
                   value={neuName} onChange={(e) => setNeuName(e.target.value)} />
            <input style={{ ...S.input, width: 320 }} placeholder="Start-Adresse, z. B. meine.signal-iduna.de"
                   value={neuUrl} onChange={(e) => setNeuUrl(e.target.value)} />
            <button style={S.btnPrimary} onClick={neuAnlegen}>Anlegen</button>
          </div>
        </div>
      )}

      {/* Liste */}
      {!aktiv && (
        <div style={S.card}>
          <div style={S.cardTitle}>Aufnahmen</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Name</th><th style={S.th}>Start</th>
                <th style={S.th}>Schritte</th><th style={S.th}>Status</th><th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {aufnahmen.length === 0 && <tr><td style={S.td} colSpan={5}>Noch keine Aufnahmen.</td></tr>}
              {aufnahmen.map((a) => (
                <tr key={a.id}>
                  <td style={S.td}><b>{a.name}</b></td>
                  <td style={{ ...S.td, fontSize: "0.78rem", color: "var(--color-gray-600)" }}>{a.start_url}</td>
                  <td style={S.td}>{a.schritt_anzahl}</td>
                  <td style={S.td}>{a.status}</td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button style={S.btn} onClick={() => sessionStarten(a, "aufnahme")}>Aufnehmen</button>
                      <button style={S.btnPrimary} disabled={!a.schritt_anzahl}
                              onClick={() => sessionStarten(a, "replay")}>Abspielen</button>
                      <button style={S.btn} disabled={!a.schritt_anzahl}
                              onClick={() => schritteBearbeiten(a)}>Schritte</button>
                      <button style={S.btn} onClick={() => robotZeigen(a)}>.robot</button>
                      <button style={{ ...S.btn, color: "var(--color-error)" }} onClick={() => loeschen(a)}>×</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Schritt-Editor (gespeicherte Aufnahme) */}
      {schrittEditor && (
        <div style={S.card}>
          <div style={{ ...S.row, justifyContent: "space-between" }}>
            <div style={S.cardTitle}>
              Schritte von „{schrittEditor.aufnahme.name}" ({schrittEditor.schritte.length})
            </div>
            <button style={S.btn} onClick={() => setSchrittEditor(null)}>schließen</button>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--color-gray-600)", marginBottom: 8 }}>
            Falsche oder doppelte Schritte mit × entfernen — die .robot-Datei wird
            danach automatisch neu erzeugt.
          </div>
          {schrittEditor.schritte.length === 0 && (
            <div style={{ fontSize: "0.85rem" }}>Keine Schritte mehr.</div>
          )}
          {schrittEditor.schritte.map((s, i) => (
            <div key={s.id || i} style={{ display: "flex", alignItems: "center", gap: 8,
                padding: "5px 8px", borderBottom: "1px solid var(--color-gray-200)",
                fontSize: "0.82rem" }}>
              <span style={{ color: "var(--color-gray-500)", minWidth: 22 }}>{i + 1}</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={s.label}>{s.label}</span>
              <button style={{ ...S.btn, padding: "2px 9px", color: "var(--color-error)" }}
                      title="Diesen Schritt entfernen"
                      onClick={() => editorSchrittWeg(i)}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* .robot-Vorschau */}
      {robotText && (
        <div style={S.card}>
          <div style={{ ...S.row, justifyContent: "space-between" }}>
            <div style={S.cardTitle}>Robot-Framework-Ablauf</div>
            <button style={S.btn} onClick={() => setRobotText(null)}>schließen</button>
          </div>
          <pre style={{ background: "var(--color-gray-100)", padding: 12, borderRadius: 6, fontSize: "0.78rem", overflowX: "auto" }}>
            {robotText}
          </pre>
        </div>
      )}
    </div>
  );
}
